import React from 'react';

import { TimeSelectorProps } from '../../types';
import { Box } from '../Box';

import LocaleDate from '../LocaleDate';
import { TimeSelectorDay } from './TimeSelectorDay';
import { formatLocaleDate, inSameTimespan } from '../../utils';
import { Carousel, CarouselSlide } from '../Carousel';
import { Timeline } from '../Timeline/Timeline';
import { useTheme } from '../../hooks';

/**
 *
 * Takes in date/time values to render a list of elements that will update map data when clicked
 *
 */
export function TimeSelector<TFeature>({
    data,
    globalDateOptions = data,
    'data-testid': dataTestId,
    platform,
    variant,
    layout = 'vertical',
    selectedValue,
    boxHeight = '36px'
}: TimeSelectorProps<TFeature>) {
    const theme = useTheme();
    const { slideWidth, margin } = theme.timeSelector;
    const selectedSlideIndex = globalDateOptions.findIndex((dateOption) =>
        inSameTimespan(dateOption.value, selectedValue, variant)
    );

    return (
        <Box
            key={platform}
            display="flex"
            data-testid={dataTestId}
            width="fit-content"
            maxWidth={layout === 'vertical' ? 'inherit' : '100%'}
            className="msat_time-selector"
        >
            <Carousel selectedIndex={selectedSlideIndex} sx={{ alignItems: 'center' }}>
                {globalDateOptions.map((dateOption, idx) => {
                    const selected = idx === selectedSlideIndex;
                    const matchingTimespan = data.find((i) => inSameTimespan(i.value, dateOption.value, variant));

                    /** If there is no data for this timespan, render an invisible box over the timeline */
                    if (!matchingTimespan) {
                        return (
                            <Box key={idx} margin={`${margin}px`} visibility={'hidden'}>
                                <CarouselSlide width={`${slideWidth}px`} />
                            </Box>
                        );
                    } else {
                        const handleItemClick = () => {
                            if (matchingTimespan.disabled || !matchingTimespan.onClick) return;
                            if (matchingTimespan.items) {
                                // If a group of dates, fire for most recent date in the timespan
                                matchingTimespan.onClick(matchingTimespan.items[matchingTimespan.items.length - 1]);
                            } else {
                                // Fire for this item's own date
                                matchingTimespan.onClick(matchingTimespan.value);
                            }
                        };

                        return (
                            <Box
                                data-testid={`date-option-${formatLocaleDate(matchingTimespan.value, undefined, variant)}`}
                                id={matchingTimespan.id}
                                key={matchingTimespan.dateKey}
                                textAlign="left"
                                margin={`${margin}px`}
                                display="flex"
                                flexDirection="column"
                                onClick={handleItemClick}
                                role="option"
                                aria-selected={selected}
                                sx={{ height: '100%' }}
                            >
                                <CarouselSlide width={`${slideWidth}px`}>
                                    <TimeSelectorDay<TFeature>
                                        height={boxHeight}
                                        color={matchingTimespan.color}
                                        selected={selected}
                                        date={<LocaleDate date={matchingTimespan.value} variant={variant} />}
                                        disabled={matchingTimespan.disabled}
                                        items={matchingTimespan.items}
                                        onDateClick={matchingTimespan.onClick}
                                        selectedValue={selectedValue}
                                    />
                                </CarouselSlide>
                            </Box>
                        );
                    }
                })}

                <Timeline activeIndex={selectedSlideIndex} globalDateOptions={globalDateOptions} variant={variant} />
            </Carousel>
        </Box>
    );
}
